import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase/supabase';
import { useAuth } from './useAuth.jsx';

export const useSession = (sessionId) => {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCollaborators = async (sessionId) => {
    try {
      const { data, error } = await supabase
        .from('collaborators')
        .select(`
          *,
          profiles:profiles(id, email, full_name, avatar_url)
        `)
        .eq('session_id', sessionId);

      if (error) throw error;
      setCollaborators(data || []);
    } catch (err) {
      console.error('Error fetching collaborators:', err);
    }
  };

  const fetchSession = useCallback(async () => {
    if (!sessionId) return;
    
    // Crucial fix: If no user, stop loading immediately
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch session data
      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;

      // Check if user has access (owner or collaborator)
      const isOwner = sessionData.owner_id === user.id;
      let isCollaborator = false;

      if (!isOwner) {
        const { data: collabData } = await supabase
          .from('collaborators')
          .select('*')
          .eq('session_id', sessionId)
          .eq('user_id', user.id)
          .single();
        isCollaborator = !!collabData;
      }

      if (!isOwner && !isCollaborator) {
        throw new Error('You do not have access to this session');
      }

      setSession(sessionData);
      setAnswers(sessionData.answers || {});

      // Fetch collaborators
      await fetchCollaborators(sessionId);
    } catch (err) {
      if (err.code !== 'PGRST116') {
        // Ignore "no rows returned" for new sessions
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, user]);

  const createSession = async (type, id = null, title = '') => {
    if (!user) return null;
    try {
      const payload = {
        type,
        owner_id: user.id,
        title: title || `${type.charAt(0).toUpperCase() + type.slice(1)} Agreement`,
        answers: {}
      };
      
      if (id) {
        payload.id = id;
      }

      const { data, error } = await supabase
        .from('sessions')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;
      setSession(data);
      return data.id;
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  const updateAnswers = async (newAnswers) => {
    try {
      const { error } = await supabase
        .from('sessions')
        .update({
          answers: { ...answers, ...newAnswers },
          updated_at: new Date().toISOString()
        })
        .eq('id', sessionId);

      if (error) throw error;
      setAnswers(prev => ({ ...prev, ...newAnswers }));
    } catch (err) {
      setError(err.message);
    }
  };

  const addCollaborator = async (email) => {
    try {
      // First, find the user by email
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email)
        .single();

      if (userError || !userData) {
        throw new Error('User not found with this email');
      }

      const { error } = await supabase
        .from('collaborators')
        .insert([
          { session_id: sessionId, user_id: userData.id, role: 'editor' }
        ]);

      if (error) throw error;

      // Refresh collaborators
      await fetchCollaborators(sessionId);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const removeCollaborator = async (userId) => {
    try {
      const { error } = await supabase
        .from('collaborators')
        .delete()
        .eq('session_id', sessionId)
        .eq('user_id', userId);

      if (error) throw error;

      // Refresh collaborators
      await fetchCollaborators(sessionId);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Subscribe to real-time changes
  useEffect(() => {
    if (!sessionId) return;

    const subscription = supabase
      .channel(`session_${sessionId}`)
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'sessions', filter: `id=eq.${sessionId}` }, 
        (payload) => {
          if (payload.new) {
            setSession(payload.new);
            setAnswers(payload.new.answers || {});
          }
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'collaborators', filter: `session_id=eq.${sessionId}` },
        () => {
          fetchCollaborators(sessionId);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [sessionId]);

  return {
    session,
    collaborators,
    answers,
    loading,
    error,
    createSession,
    updateAnswers,
    addCollaborator,
    removeCollaborator,
    refreshSession: fetchSession
  };
};